import { useEffect, useState } from "react";
import Topbar from "@/components/Topbar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Key, CheckCircle, XCircle, ArrowsClockwise, EnvelopeSimple,
  CreditCard, FloppyDisk, Eye, EyeSlash,
} from "@phosphor-icons/react";

/**
 * v3.2.65 · Admin → Integrations. One-click self-serve for rotating
 * Stripe / Resend API keys without SSH-ing to the container. Secrets
 * are encrypted at rest via `secrets_store.py`; masked in this UI.
 */
export default function AdminIntegrationsPage() {
  const [state, setState] = useState({});
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState({});

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/integrations");
      setState(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const setField = (name, field, value) => {
    setDrafts((d) => ({ ...d, [name]: { ...(d[name] || {}), [field]: value } }));
  };

  const save = async (name) => {
    const fields = drafts[name] || {};
    if (Object.values(fields).every((v) => !v || !v.trim())) {
      toast.error("Type at least one field to save.");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/admin/integrations/${name}`, { fields });
      toast.success(`${name} saved.`);
      setDrafts((d) => ({ ...d, [name]: {} }));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const test = async (name) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/integrations/${name}/test`);
      if (data.ok) {
        toast.success(`${name}: OK — ${
          name === "stripe"
            ? `mode=${data.mode} · charges_enabled=${data.charges_enabled} · payouts=${data.payouts_enabled}`
            : `${data.domains_count} domain(s) verified`
        }`);
      } else {
        toast.error(`${name}: ${data.detail || "check failed"}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test failed");
    } finally { setBusy(false); }
  };

  const drop = async (name) => {
    if (!window.confirm(`Clear all ${name} secrets? The backend will fall back to the .env value.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/integrations/${name}`);
      toast.success(`${name} cleared.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Clear failed");
    } finally { setBusy(false); }
  };

  const providers = [
    { name: "stripe", label: "Stripe", icon: CreditCard,
       fields: [
         { key: "secret_key",     label: "Secret key (sk_test_… / sk_live_…)", monospace: true },
         { key: "publishable_key", label: "Publishable key (pk_test_… / pk_live_…)", monospace: true },
         { key: "webhook_secret", label: "Webhook signing secret (whsec_…)", monospace: true },
       ] },
    { name: "resend", label: "Resend", icon: EnvelopeSimple,
       fields: [
         { key: "api_key", label: "API key (re_…)", monospace: true },
       ] },
  ];

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar />
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-10" data-testid="admin-integrations">
        <div className="label-tag">// admin</div>
        <h1 className="mt-2 font-display text-4xl md:text-5xl font-black tracking-tighter">
          Integrations
        </h1>
        <p className="mt-2 text-sm text-gray-400 max-w-xl leading-relaxed">
          Rotate API keys for the platform's third-party integrations without SSH.
          Values are encrypted at rest and take precedence over anything in the
          server's <span className="font-mono text-[#00E5FF]">.env</span> file.
          Clear a row to fall back to <span className="font-mono">.env</span>.
        </p>

        <div className="mt-8 space-y-6">
          {providers.map((p) => {
            const stored = state[p.name] || {};
            const draft  = drafts[p.name] || {};
            const Icon = p.icon;
            return (
              <section key={p.name}
                        className="border border-white/10 bg-black/40 p-5"
                        data-testid={`integ-${p.name}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <Icon size={20} weight="fill" className="text-[#00E5FF]" />
                    <h3 className="font-display text-xl font-black text-white">{p.label}</h3>
                  </div>
                  <div className="text-[10px] font-mono text-gray-500">
                    {stored._updated_at
                      ? <>updated {new Date(stored._updated_at).toLocaleString()} · <span className="text-gray-400">{stored._updated_by || ""}</span></>
                      : <>not configured in DB · falling back to <span className="text-gray-400">.env</span></>}
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  {p.fields.map((f) => {
                    const stub = stored[f.key] || "";
                    // v3.2.65 · Use the stored mask ONLY as a placeholder,
                    // never as the input's value. Otherwise the user selects/
                    // edits the mask text and the backend rejects with a
                    // confusing "No new secret values in payload." message.
                    const val  = draft[f.key] ?? "";
                    const isShow = show[`${p.name}.${f.key}`];
                    return (
                      <div key={f.key}>
                        <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">
                          {f.label}
                        </label>
                        <div className="flex gap-2">
                          <input
                            type={isShow ? "text" : "password"}
                            value={val}
                            onChange={(e) => setField(p.name, f.key, e.target.value)}
                            placeholder={stub || "not set"}
                            className={`flex-1 bg-black/40 border border-white/10 focus:border-[#00E5FF] px-3 py-2 text-sm outline-none ${f.monospace ? "font-mono" : ""} text-white`}
                            data-testid={`integ-${p.name}-${f.key}`}
                          />
                          <button type="button"
                            onClick={() => setShow((s) => ({ ...s, [`${p.name}.${f.key}`]: !isShow }))}
                            className="border border-white/10 px-3 text-gray-400 hover:text-white"
                            aria-label={isShow ? "Hide" : "Show"}
                            data-testid={`integ-${p.name}-${f.key}-toggle`}>
                            {isShow ? <EyeSlash size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <button onClick={() => save(p.name)} disabled={busy}
                    className="btn-primary text-xs py-2 px-3 inline-flex items-center gap-2 disabled:opacity-40"
                    data-testid={`integ-${p.name}-save`}>
                    <FloppyDisk size={14} weight="fill" /> Save
                  </button>
                  <button onClick={() => test(p.name)} disabled={busy}
                    className="btn-ghost text-xs py-2 px-3 inline-flex items-center gap-2 disabled:opacity-40"
                    data-testid={`integ-${p.name}-test`}>
                    <CheckCircle size={14} weight="fill" /> Test live
                  </button>
                  <button onClick={() => drop(p.name)} disabled={busy}
                    className="text-[10px] font-mono uppercase tracking-wider text-gray-500 hover:text-[#FF3B30] ml-auto"
                    data-testid={`integ-${p.name}-clear`}>
                    <XCircle size={12} className="inline" /> Clear from DB
                  </button>
                </div>
              </section>
            );
          })}
        </div>

        <div className="mt-10 border-l-2 border-[#FFCC00]/40 pl-3 text-[11px] font-mono text-gray-500 max-w-xl">
          Keys are AES-encrypted at rest with <span className="text-[#FFCC00]">SECRET_ENCRYPTION_KEY</span>.
          If you rotate that env var, existing stored secrets become unreadable —
          re-save them here after rotation.
        </div>
      </div>
    </div>
  );
}
